import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type ReactNodeViewProps } from '@tiptap/react';
import { matchEmbedBlock } from '../../lib/wiki';
import { WORK_ITEM_KEY_RE } from '../../lib/work-types';
import WorkItemCard from './WorkItemCard';

export const WORK_ITEM_EMBED_NAME = 'workItemEmbed';

function EmbedView({ node }: ReactNodeViewProps) {
  const key = typeof node.attrs.key === 'string' ? node.attrs.key : '';
  return (
    <NodeViewWrapper data-work-item-embed={key} contentEditable={false}>
      {WORK_ITEM_KEY_RE.test(key) ? (
        <WorkItemCard workItemKey={key} />
      ) : (
        <div className="my-2 rounded-md border px-3 py-2 text-sm text-destructive">Invalid work item key.</div>
      )}
    </NodeViewWrapper>
  );
}

export const WorkItemEmbed = Node.create({
  name: WORK_ITEM_EMBED_NAME,
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  // Below Paragraph so an empty document is filled with a paragraph, not an embed.
  priority: 100,

  /** Block-level tokenizer: a lone `[KEY](/browse/KEY)` line becomes a `workItemEmbed` token. */
  markdownTokenizer: {
    name: WORK_ITEM_EMBED_NAME,
    level: 'block',
    start: (src: string) => {
      const index = src.search(/(^|\n)\[[A-Za-z][A-Za-z0-9]{0,11}-\d+\]\(/);
      if (index < 0) return -1;
      return src[index] === '\n' ? index + 1 : index;
    },
    tokenize: (src: string) => {
      const match = matchEmbedBlock(src);
      if (!match) return undefined;
      return { type: WORK_ITEM_EMBED_NAME, raw: match.raw, key: match.key };
    },
  },

  addAttributes() {
    return {
      key: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-work-item-embed') ?? '',
        renderHTML: (attrs) => ({ 'data-work-item-embed': attrs.key }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-work-item-embed]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const key = typeof node.attrs.key === 'string' ? node.attrs.key : '';
    return ['div', mergeAttributes(HTMLAttributes), ['a', { href: `/browse/${key}` }, key]];
  },

  parseMarkdown(token, helpers) {
    const key = typeof token.key === 'string' ? token.key : '';
    // An empty result tells the manager to fall through to the default handling.
    if (!WORK_ITEM_KEY_RE.test(key)) return [];
    return helpers.createNode(WORK_ITEM_EMBED_NAME, { key });
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmbedView);
  },
});
